import { formatUnit } from "../lib/formatUnit";
import { RoundedUpFlag } from "./RoundedUpFlag";
// 3 decimals: a store request now carries SAP's own BOM figure (0.512 of a
// pallet), and truncating it to 0.51 hides exactly what the user asked to see.
const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const formatDateTime = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "\u2014" : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Sep 2026, user's explicit ask: a plain "Print / Save as PDF" slip
// (browser window.print(), matches GatePassPage's pattern - no backend
// change, no new library) so a requester can hand-carry the requirement
// to the store, or the store can keep a paper record - same single
// layout for both, content just reflects the request as-is. Shared by
// StoreApprovalPage.js (store-side detail view) and MyStockRequestsTab.jsx
// (requester-side "My Requests" tab) so a requester never needs the
// store_approval page permission just to print their own request.
// Layout rebuilt (same session, user provided a "Request For Store"
// reference screenshot) to match that reference: right-aligned title
// block, Store ID/Requester/Department left column + Production Order
// ID/Site/Date right column, Serial No/Product ID/Description columns
// split out instead of one combined "Component" cell. "Department" has
// no real data source yet - user's explicit choice: show Site ID there
// as a stand-in. "Issue For" from the reference is intentionally NOT
// printed (user's explicit choice - meaning wasn't applicable here).
export const RequestPrintSlip = ({ request, heading = "Goods Issue From Store" }) => {
  if (!request) return null;
  const isNpd = request.kind === "npd";
  const npdHeading = isNpd && heading === "Goods Issue From Store" ? "NPD Store Request" : heading;
  return (
    <div className="hidden print:block print:fixed print:top-0 print:left-0 print:w-full print:bg-white p-10 text-[13px] text-[#101828]" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="store-request-print-slip">
      <style>{"@media print { @page { margin: 0; size: auto; } }"}</style>
      <div className="flex justify-between items-start">
        <div />
        <div className="text-right">
          <h1 className="text-2xl font-bold tracking-wide">{npdHeading}</h1>
          <p className="text-xs text-[#667085] mt-1">Page 1 of 1</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-y-2 gap-x-10 mt-8 text-sm">
        <div><span className="font-bold">Store ID :</span> {request._id}</div>
        {isNpd ? (
          <div><span className="font-bold">Purpose :</span> {request.purpose === "consumption" ? "Production" : "NPD Project Consumption"}</div>
        ) : (
          <div><span className="font-bold">Production Order ID :</span> {request.production_proposal_id || "\u2014"}</div>
        )}
        <div><span className="font-bold">Requester :</span> {request.requester}</div>
        <div><span className="font-bold">Site :</span> {request.site_id}</div>
        <div><span className="font-bold">Department :</span> {isNpd ? "NPD" : request.site_id}</div>
        <div><span className="font-bold">Date :</span> {formatDateTime(request.created_at)}</div>
        {isNpd && (
          <div><span className="font-bold">Move Into :</span> {request.npd_target_warehouse || "\u2014"}</div>
        )}
        {isNpd && (
          <div><span className="font-bold">SAP Project :</span> {request.project_id ? `${request.project_id}${request.project_name ? ` - ${request.project_name}` : ""}` : "\u2014"}</div>
        )}
        {isNpd && (
          <div><span className="font-bold">SAP Task :</span> {request.sap_task_id ? `${request.sap_task_id}${request.sap_task_name ? ` - ${request.sap_task_name}` : ""}` : "\u2014"}</div>
        )}
        {isNpd && (
          <div><span className="font-bold">Old Customer :</span> {request.customer_name ? `${request.customer_name}${request.customer_id ? ` (${request.customer_id})` : ""}` : "\u2014"}</div>
        )}
        {isNpd && (
          <div><span className="font-bold">New Customer :</span> {request.new_customer_name || "\u2014"}</div>
        )}
        {isNpd && (
          <div className="col-span-2"><span className="font-bold">Remark :</span> {request.remark || "\u2014"}</div>
        )}
        {request.issue_id && (
          <div><span className="font-bold">Issue ID :</span> {request.issue_id}</div>
        )}
        {request.status === "resolved" && (
          <div><span className="font-bold">Issued By :</span> {request.store_actor || "\u2014"}</div>
        )}
      </div>
      <table className="w-full text-[12px] border-collapse mt-8">
        <thead>
          <tr>
            {["Serial No", "Product ID", "Description", "Required Qty", "Issued Qty", ...(isNpd ? ["Issued From"] : [])].map((h) => (
              <th key={h} className="bg-[#E5E7EB] border border-[#101828] px-2 py-1.5 text-left font-bold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(request.components || []).map((c, i) => (
            <tr key={c.product_id}>
              <td className="border border-[#101828] px-2 py-1.5 text-center">{i + 1}</td>
              <td className="border border-[#101828] px-2 py-1.5">{c.product_id}</td>
              <td className="border border-[#101828] px-2 py-1.5">{c.description || "\u2014"}</td>
              <td className="border border-[#101828] px-2 py-1.5">{formatQty(c.required_qty)} {formatUnit(c.unit_of_measure)}<RoundedUpFlag component={c} /></td>
              <td className="border border-[#101828] px-2 py-1.5">{c.issued_qty == null ? "\u2014" : `${formatQty(c.issued_qty)} ${formatUnit(c.unit_of_measure)}`}</td>
              {isNpd && (
                <td className="border border-[#101828] px-2 py-1.5">{c.issued_from_warehouse || c.chosen_source_warehouse || request.npd_target_warehouse_source || "\u2014"}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-between items-end mt-14 pt-6">
        <div className="text-xs text-[#667085]">
          <p className="border-t border-[#101828] pt-1 w-40">Requester Sign / Date</p>
        </div>
        <div className="text-xs text-[#667085]">
          <p className="border-t border-[#101828] pt-1 w-40">Store Sign / Date</p>
        </div>
      </div>
    </div>
  );
};
